import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import StickyPageHeader from '../components/common/StickyPageHeader';
import Toggle from '../components/common/Toggle';
import CarrierDetail from '../components/panels/CarrierDetail';
import { AuthContext } from '../contexts/authContext';
import { useCarrierUpdate, useSelectedCarrier } from '../hooks/apiHooks';
import Layout from '../layout';
import { UserRole } from '../models/User';

const CarrierProfilePage = ({ disableLayout, routes }) => {
  const { authUser } = useContext(AuthContext);
  const { selectedCarrier } = useSelectedCarrier();
  const carrierUpdateMutation = useCarrierUpdate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [headerActions, setHeaderActions] = useState(null);

  const isAdmin = useMemo(
    () => [UserRole.SUPER_ADMIN, UserRole.ADMIN].includes(authUser.role),
    [authUser.role]
  );

  const onEditClick = useCallback(() => setShowEditModal(true), []);
  const onClose = useCallback(() => setShowEditModal(false), []);

  const toggleCarrierActive = React.useCallback(() => {
    if (selectedCarrier) {
      carrierUpdateMutation.mutate({
        ...selectedCarrier,
        active: !selectedCarrier.active,
      });
    }
  }, [carrierUpdateMutation, selectedCarrier]);

  const onUpdate = useCallback(
    (updated) => {
      carrierUpdateMutation.mutate(updated);
    },
    [carrierUpdateMutation]
  );

  useEffect(() => {
    if (carrierUpdateMutation.isSuccess) {
      setShowEditModal(false);
    }
  }, [carrierUpdateMutation.isSuccess]);

  const content = selectedCarrier && (
    <>
      <StickyPageHeader
        actions={headerActions}
        subtitle={
          <>
            {selectedCarrier.name}
            {authUser?.role === UserRole.SUPER_ADMIN && (
              <Toggle
                defaultChecked={selectedCarrier.active}
                loading={carrierUpdateMutation.isLoading}
                size="small"
                style={{ marginLeft: 12 }}
                onChange={toggleCarrierActive}
              />
            )}
          </>
        }
        title="Profile"
      />
      <CarrierDetail
        carrier={selectedCarrier}
        id="profile"
        showEditModal={showEditModal}
        updateMutation={carrierUpdateMutation}
        onClose={onClose}
        onEditClick={onEditClick}
        onHeaderActions={setHeaderActions}
        onUpdate={onUpdate}
      />
    </>
  );

  if (disableLayout) {
    return content || null;
  }

  return (
    <Layout
      carrierLoading={!selectedCarrier}
      menuIndex={isAdmin ? 1 : 0}
      routes={routes}
      showCarriersNavigation={isAdmin}
    >
      {content}
    </Layout>
  );
};

export default CarrierProfilePage;
